import PropTypes from "prop-types";
import React from "react";

import { Text, textPropTypes } from "@swa-ui/core";
import i18n from "@swa-ui/locale";

import styles from "./SeatAssignment.module.scss";

/**
 * SeatAssignment displays the different types of seat assignment values along with defined styling and sizing
 * via the Text component.
 */
export const SeatAssignment = (props) => {
  const {
    assignedSeat,
    emphasizeAssignmentAction = false,
    lapChild,
    openSeating,
    otherAirline,
    seatAssignedAtCheckIn,
    seatNotSelected,
    seatSelectionSkipped,
    showPrefixText,
    textProps = {},
  } = props;

  return <Text {...textProps}>{renderSeatStatus()}</Text>;

  function renderSeatStatus() {
    let content;

    if (assignedSeat) {
      content = renderAssignedSeat();
    } else if (lapChild) {
      content = i18n("SeatAssignment__LAP_CHILD");
    } else if (openSeating) {
      content = i18n("SeatAssignment__OPEN_SEATING");
    } else if (otherAirline) {
      content = i18n("SeatAssignment__OTHER_AIRLINE");
    } else if (seatAssignedAtCheckIn) {
      content = (
        <>
          <Text {...getActionTextProps()}>{i18n("SeatAssignment__SEAT_ASSIGNED")}</Text>{" "}
          {i18n("SeatAssignment__AT_CHECK_IN")}
        </>
      );
    } else if (seatNotSelected) {
      content = i18n("SeatAssignment__NO_SEAT_SELECTED");
    } else if (seatSelectionSkipped) {
      content = (
        <>
          {i18n("SeatAssignment__SEAT_SELECTION")}{" "}
          <Text {...getActionTextProps()}>{i18n("SeatAssignment__SKIPPED")}</Text>
        </>
      );
    }

    return content;
  }

  function renderAssignedSeat() {
    const capitalizedAssignedSeat = assignedSeat.toUpperCase();

    return showPrefixText
      ? i18n("SeatAssignment__SEAT_ASSIGNMENT", capitalizedAssignedSeat)
      : capitalizedAssignedSeat;
  }

  function getActionTextProps() {
    return {
      ...textProps,
      bold: emphasizeAssignmentAction,
      className: styles.assignmentActionText,
    };
  }
};

SeatAssignment.propTypes = {
  /** The seat number to be displayed, if one is provided. */
  assignedSeat: PropTypes.string,

  /** Indicates if specific seat assignment text will be bolded. */
  emphasizeAssignmentAction: PropTypes.bool,

  /** Indicates if the seat is for a lap child. */
  lapChild: PropTypes.bool,

  /** Indicates if the assignment is open seating. */
  openSeating: PropTypes.bool,

  /** Indicates if the assignment is for another airline. */
  otherAirline: PropTypes.bool,

  /** Indicates if the seat is to be assigned when a customer checks in. */
  seatAssignedAtCheckIn: PropTypes.bool,

  /** Indicates if the seat has not yet been selected. */
  seatNotSelected: PropTypes.bool,

  /** Indicates if the seat selection has been skipped */
  seatSelectionSkipped: PropTypes.bool,

  /** Indicates if the prefix text of "Seat" should be displayed before the "assignedSeat". */
  showPrefixText: PropTypes.bool,

  /** Props passed to the underlying Text component. See Text for more details. */
  textProps: PropTypes.shape(textPropTypes),
};
